/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { MouseEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getDashboardsPerformanceDashboardsUrl, getReportingUrl } from '@futrli/shared-services';
import { UISwitch } from '@futrli/ui-components';

import { ConfirmModal } from '../modals/confirm-modal';
import { ConfirmTemplateActionModal } from '../modals/confirm-template-action-modal';
import { MenuSeparator } from './menus/menu-separator/menu-separator';
import { PeriodEndDropdown } from './period-end-dropdown/period-end-dropdown';
import { ReportLocaleMenu } from './menus/report-locale-menu/report-locale-menu';
import { useI18nConstants } from '../../use-i18n-constants';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { useReportBuilderContext } from '../../report-builder.context';
import { useReportsContext } from '../../reports.context';
import { useTemplatesContext } from '../../templates.context';

import styles from './top-bar.scss';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contentToPrint: any;
  onBeforeGetContent: () => void;
  onAfterPrint: () => void;
  isZoomDropdownOpen: boolean;
  isPageSetupMenuOpen: boolean;
  toggleSettingsMenu: (v: boolean) => void;
  toggleZoomMenu: (v: boolean) => void;
  isDashboard: boolean;
}
export const TopBar = ({
  contentToPrint,
  onBeforeGetContent,
  onAfterPrint,
  isZoomDropdownOpen,
  isPageSetupMenuOpen,
  toggleSettingsMenu,
  toggleZoomMenu,
  isDashboard
}: Props) => {
  const intl = useIntl();
  const flags = useFlags();
  const navigate = useNavigate();
  const { isConsolidation } = usePredictionsInfoContext();
  const { getDummyTemplateDSInReport } = useI18nConstants();

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { search } = useLocation();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [templatePendingPatch, setTemplatePendingPatch] = useState(false);
  const [isAddedToPerformanceArea, setIsAddedToPerformanceArea] = useState(false);
  const [isConfirmPublishModalOpen, setIsConfirmPublishModalOpen] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [dummyTemplateDSInReport, setDummyTemplateDSInReport] = useState<string[]>([]);

  const {
    pageSetup: { zoom },
    setZoom,
    isAutoSaving,
    isEditingTitle,
    isTemplate,
    isWorkspaceTemplate,
    isPublished,
    locale,
    reportId,
    reportDsToMap,
    reportFieldsToMap,
    setDataRefsToMap,
    setDatasetsToMap,
    setLocale,
    updateReportLocale,
    setMapTemplateDataModalOpen,
    reportTitle,
    reportData,
    setMapDatasetsModalOpen
  } = useReportBuilderContext();
  const {
    performanceDashboards,
    addPerformanceDashboard,
    removePerformanceDashboard,
    isProcessingPerformanceDashboards,
    patchReport
  } = useReportsContext();
  const { patchTemplate, getOwnerName, workspaceTemplates } = useTemplatesContext();

  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const currentTemplateStatus = isWorkspaceTemplate
    ? workspaceTemplates.find(t => t.id === reportId)?.status
    : undefined;
  const isTemplatePublished = currentTemplateStatus === 'published';

  useEffect(() => {
    if (!isFutrliTemplate) {
      setDummyTemplateDSInReport(getDummyTemplateDSInReport(reportData.pageLayoutData));
    }
  }, [reportData.pageLayoutData, isFutrliTemplate, getDummyTemplateDSInReport]);

  useEffect(() => {
    setIsAddedToPerformanceArea(!!performanceDashboards.find(pd => pd.report_id === reportId));
  }, [performanceDashboards, reportId]);

  const handlePrint = useReactToPrint({
    content: () => contentToPrint,
    onBeforeGetContent: () => undefined,
    onAfterPrint
  });

  const onPrint = () => {
    onBeforeGetContent();
    setTimeout(() => handlePrint(), 250);
  };
  const handleExit = () => {
    const queryParams = new URLSearchParams(search);
    const orgToRedirectTo = queryParams.get('org-id');
    const dashboardToRedirectTo = queryParams.get('dash-id');
    if (dashboardToRedirectTo) {
      // if the user came from a performance dashboard, they'll have dash-id query param in the URL
      // we can use it to redirect back to that performance dashboard
      navigate(getDashboardsPerformanceDashboardsUrl(workspaceId, organisationId, dashboardToRedirectTo));
    } else if (orgToRedirectTo) {
      // if the user came from a different organisation, they'll have org-id query param in the URL
      // we need to redirect back to that organisation's reporting page
      navigate(getReportingUrl(workspaceId, orgToRedirectTo, isConsolidation));
    } else {
      // otherwise, use browser history to go back to the previous page
      // this ensures users return to the correct listing page (Report Templates, My Reports, etc.)
      navigate(-1);
    }
  };

  const closePageSetupMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    toggleSettingsMenu(false);
  };

  const openPageSetupMenu = () => {
    toggleSettingsMenu(true);
  };

  const openZoomTab = () => {
    toggleZoomMenu(true);
  };

  const closeZoomTab = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    toggleZoomMenu(false);
  };

  const handleZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(+event.target.value.replace('%', '') / 100);
  };

  const onPublishTemplate = (publish: boolean) => {
    setTemplatePendingPatch(true);
    setIsPublishing(publish);
    setIsConfirmModalOpen(true);
  };
  const handlePublishingConfirm = () => {
    if (templatePendingPatch) {
      patchTemplate(reportId, { status: isPublishing ? 'published' : 'draft' }, true);
      setIsConfirmModalOpen(false);
      setTemplatePendingPatch(false);
    }
  };
  const onCloseModal = () => {
    setTemplatePendingPatch(false);
    setIsConfirmModalOpen(false);
  };

  const handleOnTriggerMapping = () => {
    if (reportDsToMap.length || reportFieldsToMap.length) {
      reportFieldsToMap.length && setDataRefsToMap(reportFieldsToMap.map(f => f.ref || f.id));
      reportDsToMap.length && setDatasetsToMap(reportDsToMap.map(ds => ds.id));
      setMapTemplateDataModalOpen(true);
    }
  };

  const handleOnTriggerDSMapping = () => {
    setDatasetsToMap(dummyTemplateDSInReport);
    setMapDatasetsModalOpen(true);
  };

  const handleChangeLocale = (newLocaleValue: string) => {
    setLocale(newLocaleValue);
    updateReportLocale(newLocaleValue);
  };

  const toggleAddToPerformanceArea = () => {
    if (!isAddedToPerformanceArea) {
      if (!isPublished) {
        setIsConfirmPublishModalOpen(true);
      } else {
        // we set the order to 1 if it's the first dashboard to be added, leaving 0th place for customers and supplies page
        addPerformanceDashboard(reportId, reportTitle, performanceDashboards.length ? null : 1);
      }
    } else {
      const dashToRemove = performanceDashboards.find(pd => pd.report_id === reportId);
      dashToRemove && removePerformanceDashboard(dashToRemove.id);
    }
  };

  const onConfirmPublish = async () => {
    setIsWorking(true);
    await patchReport(reportId, { status: 'live' });
    setIsConfirmPublishModalOpen(false);
    await addPerformanceDashboard(reportId, reportTitle, performanceDashboards.length ? null : 1);
    setIsWorking(false);
  };

  return (
    <div className={styles.topBar}>
      <div className={clsx(styles.leftSide)}>
        <span className={clsx(styles.topBarTab)} onClick={isPageSetupMenuOpen ? closePageSetupMenu : openPageSetupMenu}>
          <FormattedMessage id="reporting.page_setup" defaultMessage="Page setup" />
          {isPageSetupMenuOpen ? <Icon type="caret_up" /> : <Icon type="caret_down" />}
        </span>
        {!isDashboard && (
          <span className={clsx(styles.topBarTab)} onClick={isZoomDropdownOpen ? closeZoomTab : openZoomTab}>
            <input
              aria-label={intl.formatMessage({ id: 'common.zoom.aria_label', defaultMessage: 'Zoom in or out' })}
              type="text"
              min={10}
              max={200}
              value={`${Math.round(zoom * 100)}%`}
              onChange={handleZoomChange}
              onClick={closeZoomTab}
            />
            {isZoomDropdownOpen ? <Icon type="caret_up" /> : <Icon type="caret_down" />}
          </span>
        )}
        {isDashboard && !isTemplate && !isConsolidation && (
          <div>
            <button
              className={clsx(styles.addToPerformanceBtn, isAddedToPerformanceArea && styles.enabled)}
              onClick={toggleAddToPerformanceArea}
              disabled={isProcessingPerformanceDashboards}
            >
              {isAddedToPerformanceArea ? (
                <>
                  <Icon type="favourite" color="#007e45" mr={1} />
                  <FormattedMessage
                    id="reporting.added_to_performance_area"
                    defaultMessage="Added to Performance area"
                  />
                </>
              ) : (
                <>
                  <Icon type="favourite_lined" mr={1} />
                  <FormattedMessage id="reporting.add_to_performance_area" defaultMessage="Add to Performance area" />
                </>
              )}
            </button>
          </div>
        )}
        {isWorkspaceTemplate && (
          <span className={clsx(styles.topBarSwitch)}>
            <span
              className={clsx(styles.topBarTab, {
                [styles.deactivated]: isTemplatePublished
              })}
            >
              <FormattedMessage id="common.draft" defaultMessage="Draft" />
            </span>
            <UISwitch
              checked={isTemplatePublished}
              onChange={() => onPublishTemplate(!isTemplatePublished)}
              className={clsx(styles.switch)}
              disableRipple
            />
            <span
              className={clsx(styles.topBarTab, {
                [styles.deactivated]: !isTemplatePublished
              })}
            >
              <FormattedMessage id="common.published" defaultMessage="Published" />
            </span>
          </span>
        )}
        {flags.switchLocale && (
          <>
            {isWorkspaceTemplate && <MenuSeparator />}
            <ReportLocaleMenu value={locale} onChange={handleChangeLocale} isTemplate={isWorkspaceTemplate} />
          </>
        )}
      </div>
      <div className={clsx(styles.rightSide)} style={{ margin: 'auto 0' }}>
        {(reportDsToMap.length > 0 || reportFieldsToMap.length > 0) && (
          <div className={clsx(styles.mapIcon)}>
            <Tooltip
              message={<FormattedMessage id="reporting.click_to_map" defaultMessage="Click to map missing items" />}
            >
              <Button buttonType="tertiary" size="small" onClick={handleOnTriggerMapping} className={styles.mapButton}>
                <Icon type="warning" className={styles.buttonIcon} />
              </Button>
            </Tooltip>
          </div>
        )}
        {!isFutrliTemplate && dummyTemplateDSInReport.length > 0 && (
          <div className={clsx(styles.mapIcon)}>
            <Tooltip
              message={
                <FormattedMessage
                  id="reporting.map_datasets_or_remove"
                  defaultMessage="Map Datasets, or remove from Selected Datasets"
                />
              }
            >
              <Button
                buttonType="tertiary"
                size="small"
                onClick={handleOnTriggerDSMapping}
                className={styles.mapButton}
              >
                <Icon type="warning" className={styles.buttonIcon} />
              </Button>
            </Tooltip>
          </div>
        )}
        <PeriodEndDropdown />
        <span className={clsx(styles.saveStatus, isTemplate && styles.templateColor)}>
          <Icon
            type="save"
            tooltipMessage={<FormattedMessage id="common.autosaving" defaultMessage="Autosaving" />}
            tooltipPosition="bottom"
            tooltipVisible={isAutoSaving && isEditingTitle}
            className={clsx(styles.savingIcon, {
              [styles.animatingIcon]: isAutoSaving || isEditingTitle
            })}
          />
          {!isDashboard && (
            <Button
              size="small"
              className={clsx(styles.printBtn, styles.topBarBtn, isTemplate && styles.templateColor)}
              onClick={onPrint}
              disabled={isAutoSaving}
            >
              <FormattedMessage id="common.print" defaultMessage="Print" />
            </Button>
          )}
          <Button
            buttonType="primary"
            size="small"
            className={clsx(styles.topBarBtn, isTemplate && styles.templateColor)}
            onClick={handleExit}
            disabled={isAutoSaving || isEditingTitle}
          >
            <FormattedMessage id="common.exit" defaultMessage="Exit" />
          </Button>
        </span>
      </div>
      {isConfirmModalOpen && (
        <ConfirmTemplateActionModal
          isModalOpen={isConfirmModalOpen}
          onConfirm={handlePublishingConfirm}
          onClose={onCloseModal}
          action={isTemplatePublished ? 'unpublish' : 'publish'}
          ownerName={getOwnerName(reportId)}
        />
      )}
      {isConfirmPublishModalOpen && (
        <ConfirmModal
          onConfirm={onConfirmPublish}
          onClose={() => setIsConfirmPublishModalOpen(false)}
          isModalOpen={isConfirmPublishModalOpen}
          title={intl.formatMessage({
            id: 'reporting.dashboard_needs_publishing_first',
            defaultMessage: 'Warning: This dashboard needs to be published first!'
          })}
          content={
            <FormattedMessage
              id="reporting.dashboard_needs_publishing_content"
              defaultMessage={`This dashboard is in draft state, in order to add it to Performance area, it needs to be published.{br}
              Do you want to publish this dashboard and add it to Performance area?`}
              values={{
                br: () => (
                  <>
                    <br />
                    <br />
                  </>
                )
              }}
            />
          }
          confirmButtonText={intl.formatMessage({
            id: 'reporting.yes_publish_add',
            defaultMessage: 'Yes, publish and add'
          })}
          isLeftAligned
          isWorking={isWorking}
        />
      )}
    </div>
  );
};
